import { useMemo } from 'react'

interface CalculationsProps {
  firstAmalgamAge: string
  lastAmalgamAge: string
  amalgamTotalTeeth: string
  firstRootCanalAge: string
  lastRootCanalAge: string
  rootCanalsTotal: string
  removedTeeth: string
  firstDentalImplantAge: string
  lastDentalImplantAge: string
  implantTotalTeeth: string
  userAge?: number
}

/**
 * Hook for handling dental-related calculations
 */
export default function useDentalCalculations({
  firstAmalgamAge,
  lastAmalgamAge,
  amalgamTotalTeeth,
  firstRootCanalAge,
  lastRootCanalAge,
  rootCanalsTotal,
  removedTeeth,
  firstDentalImplantAge,
  lastDentalImplantAge,
  implantTotalTeeth,
  userAge,
}: CalculationsProps) {
  // Calculations for Amalgams
  const computedYearsExposed = useMemo(() => {
    const first = Number(firstAmalgamAge)
    if (!first) return ''
    const last = Number(lastAmalgamAge)
    if (last) return last - first
    if (userAge) return userAge - first
    return ''
  }, [firstAmalgamAge, lastAmalgamAge, userAge])

  const amalgamError = useMemo(() => {
    return computedYearsExposed !== '' && Number(computedYearsExposed) < 0
  }, [computedYearsExposed])

  const computedExposureNumber = useMemo(() => {
    const total = Number(amalgamTotalTeeth)
    const years = Number(computedYearsExposed)
    if (total && years) return (total / 2) * years
    return ''
  }, [amalgamTotalTeeth, computedYearsExposed])

  // Calculations for Root Canals
  const computedRootYearsExposed = useMemo(() => {
    const first = Number(firstRootCanalAge)
    if (!first) return ''
    const last = Number(lastRootCanalAge)
    if (last) return last - first
    if (userAge) return userAge - first
    return ''
  }, [firstRootCanalAge, lastRootCanalAge, userAge])

  const rootCanalError = useMemo(() => {
    return (
      computedRootYearsExposed !== '' && Number(computedRootYearsExposed) < 0
    )
  }, [computedRootYearsExposed])

  const computedRootCanalExposure = useMemo(() => {
    const total = Number(rootCanalsTotal)
    const years = Number(computedRootYearsExposed)
    if (total && years) return (total / 2) * years
    return ''
  }, [rootCanalsTotal, computedRootYearsExposed])

  // Calculations for Extractions/Cavitations
  const computedCaviationExposure = useMemo(() => {
    const total = Number(removedTeeth)
    if (total) return (total / 2) * 5
    return ''
  }, [removedTeeth])

  // Calculations for Implants
  const computedImplantYearsExposed = useMemo(() => {
    const first = Number(firstDentalImplantAge)
    if (!first) return ''
    const last = Number(lastDentalImplantAge)
    if (last) return last - first
    if (userAge) return userAge - first
    return ''
  }, [firstDentalImplantAge, lastDentalImplantAge, userAge])

  const computedImplantExposure = useMemo(() => {
    const total = Number(implantTotalTeeth)
    const years = Number(computedImplantYearsExposed)
    if (total && years) return (total / 2) * years
    return ''
  }, [implantTotalTeeth, computedImplantYearsExposed])

  return {
    // Amalgam calculations
    computedYearsExposed,
    amalgamError,
    computedExposureNumber,

    // Root canal calculations
    computedRootYearsExposed,
    rootCanalError,
    computedRootCanalExposure,

    // Cavitation calculations
    computedCaviationExposure,

    // Implant calculations
    computedImplantYearsExposed,
    computedImplantExposure,
  }
}
